import React, { FunctionComponent, ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// MaterialUi components
import { DataGrid, GridSelectionModel } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import LinearProgress from '@mui/material/LinearProgress';

// Components
import Separator from '../../../Separator/Separator';

// types and constants
import { UserModel } from '../../../../shared/types';
import { ROLES, USER_TABLE_COLUMNS } from '../../../../shared/constants';

// API
import { userAdminApi } from '../../../../api/userAdminApi';
import ErrorDialog from '../../../ErrorDialog/ErrorDialog';

const AssignRole: FunctionComponent<any> = (props: any) => {

    const [searchEmail, setSearchEmail] = useState<string>('');
    const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
    const [foundUsers, setFoundUsers] = useState<UserModel[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string>();
    const [selectedRole, setSelectedRole] = useState<string>('');
    const [assignRoleBtnDisabled, setAssignRoleBtnDisabled] = useState<boolean>(true);
    const [roleAssignmentSuccessMsg, setRoleAssignmentSuccessMsg] = useState<ReactNode>(null);
    const [roleAssignmentFailureMsg, setRoleAssignmentFailureMsg] = useState<ReactNode>(null);
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

    const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false);
    const [error, setError] = useState<any>();

    const navigate = useNavigate();

    const goTo = (route: string, timeout: number) => {
        setTimeout(() => {
            navigate(route);
        }, timeout * 1000);
    }

    const searchEmailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setSearchEmail(value);
    };

    const searchUser = async (event: any) => {
        setLoadingUsers(true);
        const users = await userAdminApi.getUsersByEmail(searchEmail);
        setFoundUsers(users);
    };

    const userSellectionHandler = (newSelectionModel: GridSelectionModel) => {
        setSelectedUserId(newSelectionModel[0] as string);
    };

    const roleSelectionHandler = (e: any) => {
        const { name, value } = e.target;
        setSelectedRole(value);
        setAssignRoleBtnDisabled(false);
    }

    const assignRoleHandler = async () => {
        const userToAssignRoleTo = foundUsers.find((u: UserModel) => u.id === selectedUserId);

        if (userToAssignRoleTo) {
            userToAssignRoleTo.role = selectedRole;
            try {
                const response = await userAdminApi.assignRole(userToAssignRoleTo);
                const successMsg = <p> Successfuly assigned <b>{selectedRole}</b> role to {userToAssignRoleTo.name} </p>;
                setRoleAssignmentSuccessMsg(successMsg);
                setRoleAssignmentFailureMsg(null);
                setSnackbarOpen(true);
                reset();
                goTo('/users', 3);
            } catch (error) {
                setRoleAssignmentSuccessMsg(null);
                setOpenErrorDialog(true);
                setError(error);
                reset();
            }
        }
    }

    const handleClose = () => {
        setSnackbarOpen(false);
        setOpenErrorDialog(false);
    };

    const reset = () => {
        setSearchEmail('');
        setLoadingUsers(false);
        setFoundUsers([]);
        setSelectedUserId('');
        setSelectedRole('');
        setAssignRoleBtnDisabled(true);
    }

    return (

        <div data-testid='user-table-container' className='align-center user-table-container'>
            <div className='action-header'>
                <TextField
                    id="standard-basic"
                    label="Search User and assign role"
                    variant="standard"
                    sx={{ width: 400 }}
                    value={searchEmail}
                    onChange={searchEmailChangeHandler}
                />

                <Button
                    size='small'
                    color='inherit'
                    startIcon={<SearchIcon />}
                    onClick={searchUser}
                    disabled={!searchEmail}
                />

                <FormControl variant="standard" sx={{ m: 1, minWidth: 400 }}>
                    <InputLabel id="demo-simple-select-filled-label">Role</InputLabel>
                    <Select
                        labelId="demo-simple-select-filled-label"
                        id="demo-simple-select-filled"
                        name='role'
                        value={selectedRole}
                        onChange={roleSelectionHandler}
                        disabled={!selectedUserId}
                    >
                        {
                            ROLES.map((role: string) => (
                                <MenuItem value={role} key={role}> {role} </MenuItem>
                            ))
                        }
                    </Select>
                </FormControl>
                <Button
                    data-testid='assign-role-btn'
                    variant='contained'
                    disabled={assignRoleBtnDisabled}
                    onClick={assignRoleHandler}

                >
                    Assign Role
                </Button>
            </div>

            <Separator />
            <Separator />

            {
                loadingUsers && (
                    <DataGrid
                        sx={{ border: 'none' }}
                        rows={foundUsers}
                        columns={USER_TABLE_COLUMNS}
                        density='comfortable'
                        hideFooterSelectedRowCount
                        autoHeight
                        components={{
                            LoadingOverlay: LinearProgress,
                        }}
                        loading={foundUsers.length === 0}
                        autoPageSize
                        pageSize={10}
                        // rowsPerPageOptions={[5]}
                        checkboxSelection
                        // onSelectionModelChange={sellectionHandler}
                        onSelectionModelChange={userSellectionHandler}
                    />
                )
            }

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={handleClose}
            >
                <Alert severity={roleAssignmentSuccessMsg ? "success" : "error"}>
                    {roleAssignmentSuccessMsg || roleAssignmentFailureMsg}
                </Alert>
            </Snackbar>

            {
                error &&
                (
                    <ErrorDialog
                        openErrorDialog={openErrorDialog}
                        handleClose={handleClose}
                        error={error}
                    />
                )
            }
        </div>
    )
}

export default AssignRole;